import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { NotificationsAPI } from '../api';
import { AuthContext } from './AuthContext';
import axios from 'axios';

export const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add refs for tracking API calls
  const cancelTokenRef = useRef(null);
  const requestTimeoutRef = useRef(null);
  const lastFetchTimeRef = useRef(0);
  const FETCH_COOLDOWN = 2000; // 2 seconds between requests
  
  // Fetch notifications
  const fetchNotifications = useCallback(async (params = {}) => {
    if (!currentUser) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    
    // Check if we've recently made a request
    const now = Date.now();
    if (now - lastFetchTimeRef.current < FETCH_COOLDOWN) {
      console.log('Skipping notification fetch - too soon after previous request');
      return;
    }
    
    // Cancel any pending requests
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel('Operation canceled due to new request');
    }
    
    // Create a new cancel token
    cancelTokenRef.current = axios.CancelToken.source();
    
    try {
      setLoading(true);
      setError(null);
      lastFetchTimeRef.current = now;
      
      const response = await NotificationsAPI.getNotifications(params, cancelTokenRef.current.token);
      console.log('Notifications response:', response.data);
      
      // Extract notifications using a more flexible approach
      let extractedNotifications = [];
      
      if (response.data) {
        // Try different response formats
        if (response.data.data && Array.isArray(response.data.data.notifications)) {
          // Format: { success: true, data: { notifications: [...] } }
          extractedNotifications = response.data.data.notifications;
          
          // Also update unread count if available
          if (typeof response.data.data.unread_count !== 'undefined') {
            setUnreadCount(response.data.data.unread_count);
          }
        } else if (Array.isArray(response.data.data)) {
          // Format: { success: true, data: [...] }
          extractedNotifications = response.data.data;
        } else if (Array.isArray(response.data.notifications)) {
          // Format: { notifications: [...] }
          extractedNotifications = response.data.notifications;
        } else if (Array.isArray(response.data)) {
          // Format: [...] (direct array)
          extractedNotifications = response.data;
        }
      }
      
      // Process notifications to ensure consistent read/is_read property
      const processedNotifications = extractedNotifications.map(notification => ({
        ...notification,
        // Ensure both read and is_read properties exist
        read: notification.read || notification.is_read || false,
        is_read: notification.is_read || notification.read || false
      }));
      
      setNotifications(processedNotifications);
    } catch (err) {
      // Don't report error for cancelled requests
      if (!axios.isCancel(err)) {
        console.error('Error fetching notifications:', err);
        setError('Failed to load notifications');
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser]);
  
  // Debounced version of fetchNotifications
  const debouncedFetchNotifications = useCallback((params = {}) => {
    // Clear any existing timeout
    if (requestTimeoutRef.current) {
      clearTimeout(requestTimeoutRef.current);
    }
    
    // Set a new timeout
    requestTimeoutRef.current = setTimeout(() => {
      fetchNotifications(params);
      requestTimeoutRef.current = null;
    }, 300); // 300ms debounce time
  }, [fetchNotifications]);
  
  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!currentUser) {
      setUnreadCount(0);
      return;
    }
    
    try {
      const response = await NotificationsAPI.getUnreadCount();
      console.log('Unread count response:', response.data);
      
      // Extract the unread count using a more flexible approach
      let count = 0;
      
      if (response.data) {
        // Try different response formats in order of likelihood
        if (response.data.data && typeof response.data.data.unread_count !== 'undefined') {
          // Format: { success: true, data: { unread_count: X } }
          count = response.data.data.unread_count;
        } else if (response.data.data && typeof response.data.data.count !== 'undefined') {
          // Format: { success: true, data: { count: X } }
          count = response.data.data.count;
        } else if (typeof response.data.data === 'number') {
          // Format: { success: true, data: X }
          count = response.data.data;
        } else if (typeof response.data.unread_count !== 'undefined') {
          // Format: { unread_count: X }
          count = response.data.unread_count;
        } else if (typeof response.data.count !== 'undefined') {
          // Format: { count: X }
          count = response.data.count;
        } else if (typeof response.data === 'number') {
          // Format: X (direct number)
          count = response.data;
        }
      }
      
      // Ensure count is a non-negative number
      setUnreadCount(Math.max(0, parseInt(count, 10) || 0));
    } catch (err) {
      console.error('Error fetching unread count:', err);
      // Don't reset unread count on error to prevent flickering
    }
  }, [currentUser]);
  
  // Mark notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await NotificationsAPI.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, read: true, is_read: true } 
            : notification
        )
      );
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);
  
  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      await NotificationsAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true, is_read: true }))
      );
      
      // Reset unread count
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, []);
  
  // Delete notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await NotificationsAPI.deleteNotification(notificationId);
      
      // Update local state
      const removedNotification = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      
      // Update unread count if the deleted notification was unread
      if (removedNotification && (!removedNotification.read && !removedNotification.is_read)) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [notifications]);
  
  // Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    try {
      await NotificationsAPI.deleteAllNotifications();
      
      // Update local state
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Error deleting all notifications:', err);
    }
  }, []);
  
  // Initial fetch when user changes
  useEffect(() => {
    if (currentUser) {
      fetchNotifications();
      fetchUnreadCount();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
    
    // Cleanup function
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
      }
      if (requestTimeoutRef.current) {
        clearTimeout(requestTimeoutRef.current);
      }
    };
  }, [currentUser, fetchNotifications, fetchUnreadCount]);
  
  // Set up polling for unread count (every 60 seconds)
  useEffect(() => {
    if (!currentUser) return;
    
    const intervalId = setInterval(() => {
      fetchUnreadCount();
    }, 60000); // 60 seconds (once per minute)
    
    return () => clearInterval(intervalId);
  }, [currentUser, fetchUnreadCount]);
  
  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        error,
        fetchNotifications: debouncedFetchNotifications, // Use debounced version
        fetchUnreadCount,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}; 